import { motion, useReducedMotion } from "motion/react";
import objDesignCode from "@/assets/home/obj-design-code.webp";
import objPrism from "@/assets/home/obj-prism.webp";
import objBubbles from "@/assets/home/obj-bubbles.webp";
import objPause from "@/assets/home/obj-pause.webp";

const CELLS = [
  {
    t: "Designers who write the code",
    b: "The person who draws the screen builds it. Nothing gets lost in a handover, and nothing gets flattened by one.",
    img: objDesignCode,
    alt: "A frosted glass pen nib fused to a glass cube",
    span: "lg:col-span-7",
    tone: "bg-secondary/70 ring-foreground/[0.05]",
    size: "w-[34%] max-w-[230px] right-6 top-5 md:right-8",
    drift: 8,
  },
  {
    t: "Fast to build. Faster to load.",
    b: "Edge-rendered, accessible and search-ready before launch. Performance is a design decision here, not a fix afterwards.",
    img: objPrism,
    alt: "A frosted glass prism splitting a beam of blue light",
    span: "lg:col-span-5",
    tone: "bg-electric/[0.06] ring-electric/10",
    size: "w-[38%] max-w-[190px] right-5 top-4",
    drift: 6,
  },
  {
    t: "One call a week",
    b: "Slack in between. You watch progress land on the calendar instead of waiting for a reveal.",
    img: objBubbles,
    alt: "Two overlapping frosted glass speech bubbles",
    span: "lg:col-span-5",
    tone: "bg-background ring-border",
    size: "w-[48%] max-w-[230px] right-5 top-5",
    drift: 7,
  },
  {
    t: "Launch, then decide",
    b: "No retainer. Walk away on day 14 with everything you own, or keep the studio for brand, motion and the next sprint.",
    img: objPause,
    alt: "A frosted glass pause symbol",
    span: "lg:col-span-7",
    tone: "bg-ink text-paper ring-ink",
    size: "w-[26%] max-w-[180px] right-8 top-5 md:right-10",
    drift: 9,
  },
] as const;

/** The team model, one idea per tile, each with its own glass object. */
export function WhyUs() {
  const reduce = useReducedMotion();
  return (
    <section
      aria-labelledby="why-h"
      className="relative mx-auto max-w-[1400px] border-t border-border/60 px-6 py-20 md:px-10 md:py-32"
    >
      <div className="max-w-[46ch]">
        <h2 id="why-h" className="fluid-h2 font-display tracking-[-0.035em]">
          Studio quality at sprint speed
        </h2>
        <p className="mt-5 text-pretty text-lg leading-snug text-muted-foreground md:text-xl">
          Faster than the agencies that design this well. Better designed than the shops that ship
          this fast.
        </p>
      </div>

      <ul className="mt-12 grid gap-5 lg:grid-cols-12">
        {CELLS.map((c, i) => {
          const dark = c.tone.includes("bg-ink");
          return (
            <motion.li
              key={c.t}
              initial={reduce ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              className={`relative flex min-h-[20rem] flex-col justify-end overflow-hidden rounded-[2rem] p-7 ring-1 md:min-h-[24rem] md:p-9 ${c.tone} ${c.span}`}
            >
              <motion.img
                src={c.img}
                alt={c.alt}
                width={720}
                height={720}
                loading="lazy"
                decoding="async"
                draggable={false}
                animate={reduce ? undefined : { y: [0, -c.drift, 0] }}
                transition={
                  reduce
                    ? undefined
                    : { duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }
                }
                className={`pointer-events-none absolute select-none rounded-none object-contain ${c.size}`}
              />
              <h3 className="relative max-w-[14ch] font-display text-2xl tracking-[-0.03em] leading-[1.05] md:text-3xl">
                {c.t}
              </h3>
              <p
                className={`relative mt-3 max-w-[30ch] text-pretty text-base leading-relaxed ${
                  dark ? "text-paper/70" : "text-muted-foreground"
                }`}
              >
                {c.b}
              </p>
            </motion.li>
          );
        })}
      </ul>
    </section>
  );
}
